// useMessages — data hooks + action helpers for Chatrone Messages.
//
//   useThreads()              → left-rail thread list (spaces + DMs + record threads)
//                               with per-thread unread state, live via Realtime.
//   useThreadMessages(id)     → messages in one thread, live via Realtime.
//   useUnreadThreadCount()    → nav badge (count of threads with unread messages).
//
// Action helpers (return the inserted/updated row, throw on hard error):
//   sendMessage, markThreadRead, getOrCreateRecordThread, getOrCreateDM,
//   createSpace, ensureParticipant.
//
// Threads are visible to the whole (trusted) team per the permissive RLS posture;
// DMs are filtered to their participants at the app layer.

import { useState, useEffect, useCallback, useRef, useId } from 'react'
import { supabase } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'

const FALLBACK_POLL_MS = 90_000

// ─── Thread list ─────────────────────────────────────────────────────────────

export function useThreads(currentUserId) {
  const cacheKey = 'messages:threads'
  const [threads, setThreads] = useState(() => getCached(cacheKey) || [])
  const [loading, setLoading] = useState(!getCached(cacheKey))
  const [error, setError] = useState(null)
  const channelId = useId()
  const meRef = useRef(currentUserId)
  useEffect(() => { meRef.current = currentUserId }, [currentUserId])

  const fetchThreads = useCallback(async () => {
    const me = meRef.current
    try {
      const [{ data: rawThreads, error: tErr }, { data: parts, error: pErr }] = await Promise.all([
        supabase
          .from('message_threads')
          .select('*')
          .eq('archived', false)
          .order('last_message_at', { ascending: false }),
        supabase
          .from('thread_participants')
          .select('thread_id, user_id, last_read_at, muted'),
      ])
      if (tErr) throw tErr
      if (pErr) throw pErr

      const partsByThread = {}
      for (const p of parts || []) {
        ;(partsByThread[p.thread_id] ||= []).push(p)
      }

      const visible = (rawThreads || [])
        .filter(t => {
          // DMs are private to their participants; spaces + record threads are team-wide.
          if (t.kind !== 'dm') return true
          return (partsByThread[t.id] || []).some(p => p.user_id === me)
        })
        .map(t => {
          const tp = partsByThread[t.id] || []
          const mine = tp.find(p => p.user_id === me)
          const lastRead = mine?.last_read_at ? new Date(mine.last_read_at).getTime() : 0
          const lastMsg = t.last_message_at ? new Date(t.last_message_at).getTime() : 0
          return {
            ...t,
            participants: tp,
            otherParticipantIds: tp.filter(p => p.user_id !== me).map(p => p.user_id),
            muted: !!mine?.muted,
            unread: !!t.last_message_preview && lastMsg > lastRead,
          }
        })

      setThreads(visible)
      setCached(cacheKey, visible)
      setError(null)
    } catch (e) {
      console.warn('[useThreads]', e?.message)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  // Refetch on mount and whenever the current user resolves (the filter that
  // decides which DMs are visible + per-thread unread depends on it).
  useEffect(() => { fetchThreads() }, [fetchThreads, currentUserId])

  // Realtime — any thread or message change refreshes the list (cheap for a small team).
  useEffect(() => {
    const channel = supabase
      .channel(`messages-threads-${channelId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_threads' }, () => fetchThreads())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'thread_participants' }, () => fetchThreads())
      // Also refresh on any new message: the bump trigger updates the parent
      // thread's preview/last_message_at, but a message INSERT is the most
      // reliable live signal (verified live; the thread UPDATE event can lag).
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => fetchThreads())
      .subscribe()
    const h = setInterval(fetchThreads, FALLBACK_POLL_MS)
    return () => { clearInterval(h); supabase.removeChannel(channel) }
  }, [channelId, fetchThreads])

  return { threads, loading, error, refetch: fetchThreads }
}

// ─── Messages within a thread ────────────────────────────────────────────────

export function useThreadMessages(threadId) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(!!threadId)
  const [error, setError] = useState(null)
  const channelId = useId()

  const fetchMessages = useCallback(async () => {
    if (!threadId) { setMessages([]); setLoading(false); return }
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('*')
        .eq('thread_id', threadId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(500)
      if (err) throw err
      setMessages(data || [])
      setError(null)
    } catch (e) {
      console.warn('[useThreadMessages]', e?.message)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [threadId])

  useEffect(() => { setLoading(!!threadId); fetchMessages() }, [fetchMessages, threadId])

  useEffect(() => {
    if (!threadId) return
    const channel = supabase
      .channel(`messages-thread-${threadId}-${channelId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `thread_id=eq.${threadId}` },
        payload => {
          setMessages(prev => (prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]))
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages', filter: `thread_id=eq.${threadId}` },
        payload => setMessages(prev => prev.map(m => (m.id === payload.new.id ? payload.new : m))))
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [threadId, channelId])

  return { messages, loading, error, refetch: fetchMessages }
}

// ─── Unread badge ────────────────────────────────────────────────────────────

export function useUnreadThreadCount(currentUserId) {
  const { threads } = useThreads(currentUserId)
  const unread = threads.filter(t => t.unread && !t.muted).length
  return { unread }
}

// ─── Actions ─────────────────────────────────────────────────────────────────

export async function sendMessage({ threadId, body, senderUserId, senderName, mentions = [], replyToId = null, companyId = 'chatrone_us', attachments = [] }) {
  const text = (body || '').trim()
  if (!threadId || (!text && !attachments.length)) throw new Error('threadId and body are required')
  const { data, error } = await supabase
    .from('messages')
    .insert({
      thread_id: threadId,
      sender_user_id: senderUserId,
      sender_type: 'user',
      sender_name: senderName || null,
      body: text,
      mentions,
      reply_to_id: replyToId,
      company_id: companyId,
      attachments,
    })
    .select()
    .single()
  if (error) throw error
  // Mark my own read pointer forward so my own message doesn't show as unread.
  if (senderUserId) await markThreadRead(threadId, senderUserId).catch(() => {})
  // Fire-and-forget off-app notification dispatch (web push / native / email).
  // In-app toast is handled separately by the Realtime subscription.
  notifyMessage(data.id)
  return data
}

// Best-effort: tell the server to fan out off-app notifications for a message.
// Never throws into the send path — push is a nice-to-have, not a blocker.
async function notifyMessage(messageId) {
  if (!messageId) return
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) return
    fetch('/api/notify-message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message_id: messageId }),
    }).catch(() => {})
  } catch { /* offline / no session — skip */ }
}

export async function markThreadRead(threadId, userId) {
  if (!threadId || !userId) return
  const { error } = await supabase
    .from('thread_participants')
    .upsert(
      { thread_id: threadId, user_id: userId, last_read_at: new Date().toISOString() },
      { onConflict: 'thread_id,user_id' },
    )
  if (error) throw error
}

export async function ensureParticipant(threadId, userId, { role = 'member' } = {}) {
  if (!threadId || !userId) return
  await supabase
    .from('thread_participants')
    .upsert({ thread_id: threadId, user_id: userId, role }, { onConflict: 'thread_id,user_id', ignoreDuplicates: true })
}

// Get-or-create the single ongoing thread for a record (contact/project/…).
export async function getOrCreateRecordThread({ subjectType, subjectId, subjectLabel, createdBy, companyId = 'chatrone_us' }) {
  if (!subjectType || !subjectId) throw new Error('subjectType and subjectId are required')
  const { data: existing } = await supabase
    .from('message_threads')
    .select('*')
    .eq('kind', 'record')
    .eq('subject_type', subjectType)
    .eq('subject_id', subjectId)
    .maybeSingle()
  if (existing) {
    // Keep the display label fresh if the record was renamed.
    if (subjectLabel && existing.subject_label !== subjectLabel) {
      await supabase.from('message_threads').update({ subject_label: subjectLabel }).eq('id', existing.id)
      existing.subject_label = subjectLabel
    }
    return existing
  }
  const { data, error } = await supabase
    .from('message_threads')
    .insert({ kind: 'record', subject_type: subjectType, subject_id: subjectId, subject_label: subjectLabel, created_by: createdBy, company_id: companyId })
    .select()
    .single()
  if (error) {
    // Unique-index race: another tab created it first — fetch and return.
    const { data: race } = await supabase
      .from('message_threads').select('*')
      .eq('kind', 'record').eq('subject_type', subjectType).eq('subject_id', subjectId).maybeSingle()
    if (race) return race
    throw error
  }
  return data
}

// Get-or-create a 1:1 DM between two users (participant-set match).
export async function getOrCreateDM({ userIds, createdBy, companyId = 'chatrone_us' }) {
  const ids = [...new Set(userIds)].filter(Boolean)
  if (ids.length < 2) throw new Error('a DM needs at least two distinct users')

  // Find an existing DM whose participant set is exactly these users.
  const { data: candidates } = await supabase
    .from('thread_participants')
    .select('thread_id, user_id')
    .in('user_id', ids)
  const byThread = {}
  for (const r of candidates || []) (byThread[r.thread_id] ||= new Set()).add(r.user_id)
  const matchThreadIds = Object.entries(byThread)
    .filter(([, set]) => set.size === ids.length && ids.every(id => set.has(id)))
    .map(([tid]) => tid)
  if (matchThreadIds.length) {
    const { data: dm } = await supabase
      .from('message_threads').select('*')
      .in('id', matchThreadIds).eq('kind', 'dm').limit(1).maybeSingle()
    if (dm) return dm
  }

  const { data: thread, error } = await supabase
    .from('message_threads')
    .insert({ kind: 'dm', created_by: createdBy, company_id: companyId })
    .select()
    .single()
  if (error) throw error
  await supabase
    .from('thread_participants')
    .insert(ids.map(uid => ({ thread_id: thread.id, user_id: uid })))
  return thread
}

export async function createSpace({ name, topic, icon, isPrivate = false, memberIds = [], createdBy, companyId = 'chatrone_us' }) {
  if (!name?.trim()) throw new Error('space name is required')
  const { data: thread, error } = await supabase
    .from('message_threads')
    .insert({ kind: 'space', name: name.trim(), topic: topic || null, icon: icon || null, is_private: isPrivate, created_by: createdBy, company_id: companyId })
    .select()
    .single()
  if (error) throw error
  const ids = [...new Set([createdBy, ...memberIds])].filter(Boolean)
  if (ids.length) {
    await supabase
      .from('thread_participants')
      .insert(ids.map(uid => ({ thread_id: thread.id, user_id: uid, role: uid === createdBy ? 'admin' : 'member' })))
  }
  return thread
}
